import React, { useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * BookingPaymentDialog — Opens after a PAYG parent completes a booking.
 *
 * Presents two payment paths:
 *
 *   1. **Card / Mollie** — hosted checkout. We open the returned URL in
 *      the same tab; on return, the dashboard reloads and the badge
 *      flips to "Paid" once the webhook lands.
 *
 *   2. **Bank Transfer** — we surface the organization's bank details
 *      + a per-booking reference the parent must quote. The booking
 *      stays as "pending_bank_transfer" until an admin confirms
 *      receipt in the Pending Payments section.
 *
 * Props
 *   open        — boolean
 *   onClose     — called when the dialog is dismissed / done
 *   booking     — the booking that needs payment (needs _id + amount)
 *   childName   — display name for the header
 *   sessionLabel — human-friendly "Session for <date>" line
 */
export function BookingPaymentDialog({
  open,
  onClose,
  booking,
  childName,
  sessionLabel,
}) {
  const [phase, setPhase] = useState('choose'); // 'choose' | 'bank' | 'busy'
  const [bank, setBank] = useState(null);       // { bank_details, reference, amount }
  const [error, setError] = useState('');

  if (!open || !booking) return null;

  const amount = Number(booking.payment_amount ?? booking.price_paid ?? 0);

  const handleCardPay = async () => {
    setError('');
    setPhase('busy');
    try {
      const res = await api.startBookingMolliePayment(booking._id);
      if (!res?.checkout_url) throw new Error('Card gateway returned no URL');
      // Same-tab redirect — Mollie brings the user back to a dashboard
      // route (?booking_paid=<id>) once complete.
      window.location.href = res.checkout_url;
    } catch (err) {
      setError(err?.message || 'Card payment failed');
      setPhase('choose');
    }
  };

  const handleBankChoose = async () => {
    setError('');
    setPhase('busy');
    try {
      const res = await api.startBookingBankTransfer(booking._id);
      setBank(res);
      setPhase('bank');
    } catch (err) {
      setError(err?.message || 'Bank transfer setup failed');
      setPhase('choose');
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed — please select and copy manually.');
    }
  };

  return (
    <div className="modal-overlay" data-testid="booking-payment-dialog" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560, width: '100%' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div>
            <h2 style={{ margin: 0 }}>💳 Pay for booking</h2>
            <small style={{ color: 'hsl(var(--muted-foreground))' }}>
              {childName || 'Booking'} — <strong>£{amount.toFixed(2)}</strong>
              {sessionLabel && <> · {sessionLabel}</>}
            </small>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            aria-label="Close payment dialog"
            data-testid="booking-payment-close"
          >
            ✕
          </button>
        </div>

        {error && (
          <div
            className="alert alert-error"
            data-testid="booking-payment-error"
            style={{
              background: 'hsl(0 70% 95%)', color: 'hsl(0 70% 35%)',
              padding: '0.6rem 0.85rem', borderRadius: 6,
              marginBottom: '0.75rem', fontSize: '0.85rem',
            }}
          >
            {error}
          </div>
        )}

        {phase === 'choose' && (
          <>
            <p style={{ margin: '0 0 1rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.9rem' }}>
              Your seat is held. Please choose how you'd like to pay:
            </p>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCardPay}
                data-testid="booking-payment-card"
                style={{ padding: '0.9rem 1rem', textAlign: 'left', fontSize: '0.95rem' }}
              >
                💳 Pay by card / debit
                <div style={{ fontSize: '0.75rem', opacity: 0.85, marginTop: '0.15rem', fontWeight: 400 }}>
                  Secure hosted checkout · booking confirmed instantly
                </div>
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleBankChoose}
                data-testid="booking-payment-bank"
                style={{ padding: '0.9rem 1rem', textAlign: 'left', fontSize: '0.95rem' }}
              >
                🏦 Pay by bank transfer
                <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.15rem', fontWeight: 400 }}>
                  We'll show the office's bank details · booking stays pending until they confirm
                </div>
              </button>
            </div>
            <div style={{ marginTop: '0.85rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
              You can close this window and pay later from your bookings list.
            </div>
          </>
        )}

        {phase === 'busy' && (
          <div className="loading" data-testid="booking-payment-busy" style={{ padding: '1rem 0' }}>
            Working…
          </div>
        )}

        {phase === 'bank' && bank && (
          <div data-testid="booking-payment-bank-details">
            <p style={{ margin: '0 0 0.75rem' }}>
              Please make a bank transfer of <strong>£{(bank.amount ?? amount).toFixed(2)}</strong> using the
              details below. Your booking will be marked <em>Paid</em> once the office
              confirms funds have arrived (usually within 1 working day).
            </p>

            {/* Reference — the most critical field, so it's highlighted */}
            <div
              style={{
                padding: '0.75rem 0.85rem',
                border: '2px solid hsl(var(--primary))',
                borderRadius: 8,
                marginBottom: '0.75rem',
                background: 'hsl(var(--primary) / 0.05)',
              }}
              data-testid="booking-payment-reference-box"
            >
              <div style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Payment reference (must be included in your transfer)
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem', gap: '0.5rem' }}>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, fontFamily: 'monospace' }} data-testid="booking-payment-reference-value">
                  {bank.reference}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => copy(bank.reference)}
                  data-testid="booking-payment-reference-copy"
                  style={{ fontSize: '0.8rem' }}
                >
                  📋 Copy
                </button>
              </div>
            </div>

            {/* Bank details grid */}
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {bank.bank_details?.account_name && (
                <BankField label="Account name" value={bank.bank_details.account_name} onCopy={copy} testId="account-name" />
              )}
              {bank.bank_details?.sort_code && (
                <BankField label="Sort code" value={bank.bank_details.sort_code} onCopy={copy} testId="sort-code" />
              )}
              {bank.bank_details?.account_number && (
                <BankField label="Account number" value={bank.bank_details.account_number} onCopy={copy} testId="account-number" />
              )}
              {bank.bank_details?.iban && (
                <BankField label="IBAN" value={bank.bank_details.iban} onCopy={copy} testId="iban" />
              )}
              {bank.bank_details?.bic && (
                <BankField label="BIC / SWIFT" value={bank.bank_details.bic} onCopy={copy} testId="bic" />
              )}
            </div>

            {bank.bank_details?.payment_instructions && (
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.6rem 0.85rem',
                  border: '1px dashed hsl(var(--border))',
                  borderRadius: 6,
                  fontSize: '0.85rem',
                  color: 'hsl(var(--muted-foreground))',
                  whiteSpace: 'pre-wrap',
                }}
                data-testid="booking-payment-bank-instructions"
              >
                {bank.bank_details.payment_instructions}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
                data-testid="booking-payment-bank-done"
              >
                I've made the transfer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BankField({ label, value, onCopy, testId }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.4rem 0.6rem',
        border: '1px solid hsl(var(--border))',
        borderRadius: 6,
      }}
      data-testid={`booking-payment-bank-${testId}`}
    >
      <div>
        <div style={{ fontSize: '0.7rem', color: 'hsl(var(--muted-foreground))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '0.95rem' }} data-testid={`booking-payment-bank-${testId}-value`}>
          {value}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-ghost"
        onClick={() => onCopy(value)}
        style={{ fontSize: '0.75rem' }}
        data-testid={`booking-payment-bank-${testId}-copy`}
      >
        📋
      </button>
    </div>
  );
}

export default BookingPaymentDialog;
